import { useCallback, useEffect, useRef, useState } from "react"
import {
  PIPELINE_STEPS,
  buildMergedScreeningResult,
  buildPhase1ScreeningResult,
  PHASE2_COVERAGE,
  PHASE2_EXTRA_INACCESSIBLE,
  PHASE2_EXTRA_LOG,
  PHASE2_INDEX_APPEND,
} from "../mocks/documentaryScreeningFixtures"

/**
 * @typedef {'idle' | 'phase1_running' | 'phase1_done' | 'phase2_running' | 'complete'} MockRunPhase
 */

function mergePhase2IntoPhase1(phase1) {
  return {
    axesMarkdown: phase1.axesMarkdown,
    searchLog: [...phase1.searchLog, ...PHASE2_EXTRA_LOG],
    coverage: PHASE2_COVERAGE.map((row) => ({ ...row })),
    indexMarkdown: `${phase1.indexMarkdown.trimEnd()}${PHASE2_INDEX_APPEND}`,
    inaccessible: [...phase1.inaccessible, ...PHASE2_EXTRA_INACCESSIBLE],
  }
}

/**
 * Frozen state for Storybook / design review (optional).
 * @typedef {object} StorySnapshot
 * @property {MockRunPhase} phase
 * @property {object} result
 * @property {number} [activeStepIndex] defaults to all steps done
 * @property {number} [elapsedMs]
 */

/**
 * @param {object} options
 * @param {'single' | 'two_phase'} options.mode
 * @param {number} [options.stepDurationMs]
 * @param {StorySnapshot | null} [options.storySnapshot]
 */
export function useDocumentaryScreeningMockRun({ mode, stepDurationMs = 1800, storySnapshot = null }) {
  /** @type {React.MutableRefObject<MockRunPhase>} */
  const phaseRef = useRef(/** @type {MockRunPhase} */ (storySnapshot?.phase ?? "idle"))
  const [phase, setPhase] = useState(() => /** @type {MockRunPhase} */ (storySnapshot?.phase ?? "idle"))
  const [activeStepIndex, setActiveStepIndex] = useState(() =>
    storySnapshot != null
      ? storySnapshot.activeStepIndex !== undefined
        ? storySnapshot.activeStepIndex
        : PIPELINE_STEPS.length
      : -1
  )
  const [elapsedMs, setElapsedMs] = useState(() => storySnapshot?.elapsedMs ?? 0)
  const [result, setResult] = useState(() => storySnapshot?.result ?? null)
  const timerRefs = useRef([])
  const rafRef = useRef(null)
  const startTimeRef = useRef(0)

  const clearTimers = useCallback(() => {
    timerRefs.current.forEach((id) => clearTimeout(id))
    timerRefs.current = []
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const setPhaseSafe = useCallback((p) => {
    phaseRef.current = p
    setPhase(p)
  }, [])

  const tickElapsed = useCallback(() => {
    const loop = () => {
      setElapsedMs(Date.now() - startTimeRef.current)
      rafRef.current = requestAnimationFrame(loop)
    }
    rafRef.current = requestAnimationFrame(loop)
  }, [])

  const runStepSequence = useCallback(
    (onDone) => {
      clearTimers()
      setActiveStepIndex(-1)
      let i = 0
      const advance = () => {
        if (i >= PIPELINE_STEPS.length) {
          setActiveStepIndex(PIPELINE_STEPS.length)
          if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = null
          }
          onDone()
          return
        }
        setActiveStepIndex(i)
        const t = setTimeout(() => {
          i += 1
          advance()
        }, stepDurationMs)
        timerRefs.current.push(t)
      }
      advance()
    },
    [clearTimers, stepDurationMs]
  )

  const startPhase1 = useCallback(() => {
    if (phaseRef.current !== "idle" && phaseRef.current !== "complete") return
    clearTimers()
    setResult(null)
    setElapsedMs(0)
    startTimeRef.current = Date.now()
    tickElapsed()
    setPhaseSafe("phase1_running")
    runStepSequence(() => {
      if (mode === "single") {
        setResult(buildMergedScreeningResult())
        setPhaseSafe("complete")
      } else {
        setResult(buildPhase1ScreeningResult())
        setPhaseSafe("phase1_done")
      }
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      setElapsedMs(Date.now() - startTimeRef.current)
    })
  }, [clearTimers, mode, runStepSequence, setPhaseSafe, tickElapsed])

  const startPhase2 = useCallback(() => {
    if (phaseRef.current !== "phase1_done" || !result) return
    clearTimers()
    startTimeRef.current = Date.now()
    setElapsedMs(0)
    tickElapsed()
    setPhaseSafe("phase2_running")
    runStepSequence(() => {
      setResult((prev) => mergePhase2IntoPhase1(prev))
      setPhaseSafe("complete")
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      setElapsedMs(Date.now() - startTimeRef.current)
    })
  }, [clearTimers, result, runStepSequence, setPhaseSafe, tickElapsed])

  const reset = useCallback(() => {
    clearTimers()
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    setPhaseSafe("idle")
    setActiveStepIndex(-1)
    setElapsedMs(0)
    setResult(null)
  }, [clearTimers, setPhaseSafe])

  useEffect(() => () => clearTimers(), [clearTimers])

  const isRunning = phase === "phase1_running" || phase === "phase2_running"

  /** For JSON export / backend contract discussions */
  const exportPayload = useCallback(
    (formValues) => ({
      mode,
      phase,
      form: formValues,
      result,
      meta: {
        pipelineSteps: PIPELINE_STEPS.length,
        searchLogCount: result?.searchLog?.length ?? 0,
      },
    }),
    [mode, phase, result]
  )

  return {
    phase,
    activeStepIndex,
    elapsedMs,
    result,
    isRunning,
    startPhase1,
    startPhase2,
    reset,
    exportPayload,
  }
}
